import { useContext, useRef } from 'react';
import useProperties from '../../../api/properties/useProperties';
import { useUser } from '../../../api/user/useUser';
import { useIsSnowing } from '../../../api/weather/useWeather';
import { settledBottomNavItemContext } from '../../../components/Navigation/settledBottomNavItemContext';
import { RiveHome } from '../../../components/RiveHome/RiveHome';
import { DeviceStatus, PoweredDeviceKey } from '../../../components/RiveHome/RiveHomeView';
import { useElementSize } from '../../../components/SunnyChat/useElementSize';
import { HomeHeroScene } from './homeHeroScene';

const RIVE_ARTBOARD_SIZE = { width: 3500, height: 2625 };
const SCENE_WIDTH_PER_HERO_WIDTH = 2.46;
const HOUSE_ANCHOR = { x: 1711, y: 1895, offsetFromHeroBottom: 164 };

interface HomeHeroViewProps {
  scene?: HomeHeroScene;
  coordinates?: { lat: number; lng: number };
  snow: boolean;
  isActive: boolean;
}

export const HomeHeroView = ({ scene, coordinates, snow, isActive }: HomeHeroViewProps) => {
  const isPowerLineVisible = (device: PoweredDeviceKey) => {
    const status: DeviceStatus | undefined = scene?.devices[device];
    return Boolean(
      scene?.arePowerLinesVisible && status && !('installInProgress' in status && status.installInProgress),
    );
  };

  const heroRef = useRef<HTMLDivElement>(null);
  const { width: heroWidth } = useElementSize(heroRef);
  const sceneScale = (heroWidth * SCENE_WIDTH_PER_HERO_WIDTH) / RIVE_ARTBOARD_SIZE.width;

  return (
    <div ref={heroRef} className="relative h-full w-full">
      {heroWidth > 0 && (
        <div
          className="absolute"
          style={{
            width: RIVE_ARTBOARD_SIZE.width * sceneScale,
            height: RIVE_ARTBOARD_SIZE.height * sceneScale,
            left: heroWidth / 2 - HOUSE_ANCHOR.x * sceneScale,
            bottom: HOUSE_ANCHOR.offsetFromHeroBottom - (RIVE_ARTBOARD_SIZE.height - HOUSE_ANCHOR.y) * sceneScale,
          }}
        >
          <RiveHome
            coordinates={coordinates}
            snow={snow}
            precipitation={false}
            lightning={false}
            clouds="none"
            planning={false}
            solarObstructed={false}
            devices={scene?.devices ?? {}}
            powerLines={{
              solar: isPowerLineVisible('solar'),
              inverter: isPowerLineVisible('inverter'),
              battery: isPowerLineVisible('battery'),
              generator: isPowerLineVisible('generator'),
              grid: isPowerLineVisible('grid'),
              evCharger: isPowerLineVisible('evCharger'),
            }}
            deviceZoom={scene?.deviceZoom}
            isActive={isActive}
          />
        </div>
      )}
    </div>
  );
};

export const HomeHero = ({ scene }: { scene?: HomeHeroScene }) => {
  const { user, isLoading: isUserLoading } = useUser();
  const propertyId = user?.propertyIds?.[0];
  const { data: property, isLoading: isPropertyLoading } = useProperties(propertyId);
  const settledBottomNavItemId = useContext(settledBottomNavItemContext);
  const { isSnowing, isLoading: isWeatherLoading } = useIsSnowing(propertyId);
  const isWaitingForScene = isUserLoading || (Boolean(propertyId) && isPropertyLoading) || isWeatherLoading;

  return (
    !isWaitingForScene && (
      <HomeHeroView
        scene={scene}
        coordinates={property?.address?.coordinates}
        snow={isSnowing}
        isActive={settledBottomNavItemId === 'home'}
      />
    )
  );
};
